import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useApi } from '@src/app/context/Api';
import { AppliedFilterCriteria } from '@src/entities/Criteria/ui/components/AppliedFilterCriteria';
import { RiskCodesTooltip } from '@src/entities/RiskRecord/ui/components/tooltips/RiskCodes';
import { SourcesTooltip } from '@src/entities/Source/ui/components/tooltips/Sources';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { useInterval } from '@uhg-abyss/web/hooks/useInterval';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isArray, isEmpty, isUndefined, merge } from 'lodash';
import { uniq } from 'lodash/array';
import React, { useEffect, useMemo, useState } from 'react';

import { ExpansionRow } from './components/ExpansionRow';
import { Header } from './components/Header';
import { ExportStatus } from './components/Header/components/ExportStatus';
import { TotalRecords } from './components/Header/components/TotalRecords';
import configuration from './includes/configuration.json';

/**
 * Table: RiskRecords
 *
 * @TODO - Needs description.
 *
 * @param allowExport
 * @param context
 * @param criteria
 * @param currentEntity
 * @param showAnalyzeRisks
 * @param showFilters
 * @param showHeaderLeft
 * @param showHeaderTop
 * @param showTotalRecords
 * @returns {Element}
 * @constructor
 */
export function RiskRecords({
  allowExport = false,
  context = 'entrance',
  criteria = {
    entrance: {
      additional: [],
      common: [],
      commonCriteriaVersionIds: [],
      commonIds: [],
      merged: [],
    },
  },
  currentEntity = {
    id: '',
    type: '',
  },
  showAnalyzeRisks = false,
  showFilters = false,
  showHeaderLeft = true,
  showHeaderTop = true,
  showTotalRecords = false,
}) {
  const { clearApiCache, useApiQuery } = useApi('RiskRecord');

  const [ListRiskRecords, { data, error, isFetching, isLoading }] = useApiQuery('ListRiskRecords');
  const [ListRiskCodes, { data: riskCodes, isFetching: riskCodesFetching, isLoading: riskCodesLoading }] =
    useApiQuery('ListRiskCodes');
  const [GetRiskRecordsCount, { data: riskRecordsCount }] = useApiQuery('GetRiskRecordsCount');
  const [GetExportDetails, { data: exportData, refetch: exportRefetch }] = useApiQuery('GetExportDetails');

  const [pollingRate, setPollingRate] = useState(null);

  /**
   * Get the list of risk codes if not already fetched.
   */
  useEffect(() => {
    if (isUndefined(riskCodes)) {
      ListRiskCodes();
    }
  }, [riskCodes]);

  /**
   * Get the total number of risk records if not already fetched.
   */
  useEffect(() => {
    if (showHeaderTop === true && showTotalRecords === true) {
      if (isUndefined(riskRecordsCount) && !isEmpty(criteria?.[context]?.merged)) {
        if (context === 'entrance') {
          GetRiskRecordsCount({
            criteria: {
              entrance: criteria?.entrance,
            },
          });
        }

        if (context === 'exit') {
          GetRiskRecordsCount({ criteria });
        }
      }
    }
  }, [riskRecordsCount, criteria, showTotalRecords, showHeaderTop]);

  /**
   * Get export details if not already fetched.
   */
  useEffect(() => {
    if (allowExport === true && showHeaderTop === true && showHeaderLeft === true) {
      if (isUndefined(exportData) && currentEntity?.type === 'actionPath' && currentEntity?.id) {
        GetExportDetails({ actionPathId: currentEntity?.id });
      }
    }
  }, [exportData, currentEntity, showHeaderTop, showHeaderLeft, allowExport]);

  /**
   * Start polling for status changes.
   */
  useEffect(() => {
    if (allowExport === true && showHeaderTop === true && showHeaderLeft === true) {
      if (['RUNNING', 'SCHEDULED'].includes(exportData?.status)) {
        setPollingRate(15000);
      } else {
        setPollingRate(null);
      }
    }
  }, [exportData, showHeaderTop, showHeaderLeft, allowExport]);

  useInterval(async () => {
    if (allowExport === true && showHeaderTop === true && showHeaderLeft === true) {
      await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['GetExportDetails'] }]);
      GetExportDetails({ actionPathId: currentEntity?.id });
    }
  }, pollingRate);

  /**
   * renderCellEID
   *
   * renders a link to open in a remote system (HCM - Health Care Matrix)
   *
   * @param cell
   * @returns {JSX.Element}
   */
  const renderCellEID = ({ cell }) => {
    return <Link href={`/analysis/eid-search/${cell.value}/`}>{cell.value}</Link>;
  };

  /**
   * renderCellRiskScore
   *
   * @TODO - Needs description.
   *
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellRiskScore = ({ cell }) => {
    const codeNumbers = cell?.value?.split('-');
    const letter = codeNumbers.shift().toUpperCase();
    const codeDescriptions = [];

    const codesArray = Array.isArray(riskCodes?.ListRiskCodes) ? riskCodes?.ListRiskCodes : [];

    codeNumbers.forEach((codeNumber) => {
      const match = codesArray.find((riskCode) => {
        return riskCode?.codeId === String(codeNumber);
      });
      if (!isUndefined(match) && !codeDescriptions.includes(match)) {
        codeDescriptions.push(match);
      }
    });

    if (letter === 'R') {
      return (
        <Layout.Group space={Number(themeConfiguration?.theme?.space?.md) || 16}>
          <Badge outline variant="error">
            <Tooltip content={<RiskCodesTooltip codeDetails={codeDescriptions} />} placement="auto">
              <div>{cell?.value}</div>
            </Tooltip>
          </Badge>
        </Layout.Group>
      );
    }

    if (letter === 'Y') {
      return (
        <Layout.Group space={Number(themeConfiguration?.theme?.space?.md) || 16}>
          <Badge outline variant="warning">
            <Tooltip content={<RiskCodesTooltip codeDetails={codeDescriptions} />} placement="auto">
              <div>{cell?.value}</div>
            </Tooltip>
          </Badge>
        </Layout.Group>
      );
    }

    if (letter === 'G') {
      return (
        <Layout.Group space={Number(themeConfiguration?.theme?.space?.md) || 16}>
          <Badge outline variant="success">
            {cell?.value}
          </Badge>
        </Layout.Group>
      );
    }

    return cell.value;
  };

  /**
   * renderCellTrustedSources
   *
   * @TODO - Needs description.
   *
   * @param row
   * @returns {JSX.Element|string}
   */
  const renderCellTrustedSources = ({ row }) => {
    const sources = [];

    row?.original?.ireRiskRecord?.trustedRecordSources?.forEach((trustedSource) => {
      if (!sources.includes(trustedSource?.source)) {
        sources.push(trustedSource?.source);
      }
    });

    return (
      <Tooltip content={<SourcesTooltip sources={sources} />} placement="auto">
        <span className="truncate">{sources.join(', ')}</span>
      </Tooltip>
    );
  };

  /**
   * renderCellUntrustedSources
   *
   * @TODO - Needs description.
   *
   * @param row
   * @returns {JSX.Element|string}
   */
  const renderCellUntrustedSources = ({ row }) => {
    if (isArray(row?.original?.ireRiskRecord?.remediationFindings)) {
      let sources = [];

      row?.original?.ireRiskRecord?.remediationFindings.forEach((attribute) => {
        if (isArray(attribute.untrustedRecordSources)) {
          attribute.untrustedRecordSources.forEach((untrustedSource) => {
            if (!sources.includes(untrustedSource.source)) {
              sources.push(untrustedSource.source);
            }
          });
        }
      });

      sources = uniq(sources);

      return (
        <Tooltip content={<SourcesTooltip sources={sources} />} placement="auto">
          <span className="truncate">{sources.join(', ')}</span>
        </Tooltip>
      );
    }
    return '';
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return configuration?.initialColumns.map((item) => {
      const column = item;

      if (column.Header === 'EID') {
        column.Cell = renderCellEID;
      }

      if (column.Header === 'Risk Score') {
        column.Cell = renderCellRiskScore;
      }

      if (column.Header === 'Trusted Sources') {
        column.Cell = renderCellTrustedSources;
      }

      if (column.Header === 'Untrusted Sources') {
        column.Cell = renderCellUntrustedSources;
      }

      return column;
    });
  }, [riskCodes]);

  return (
    <ErrorHandler location="src/entities/RiskRecord/ui/components/tables/RiskRecords/RiskRecords.jsx">
      <TableComponent
        {...{
          columns,
          configuration: merge({}, configuration, {
            renderSubComponent: ({ original }) => {
              return <ExpansionRow row={original} />;
            },
          }),
          error,
          headerLeft: (
            <React.Fragment>
              {showHeaderLeft === true && (
                <Header
                  allowExport={allowExport}
                  criteria={criteria}
                  currentEntity={currentEntity}
                  exportDetails={exportData}
                  exportRefetch={exportRefetch}
                  pollingRate={pollingRate}
                  riskRecordCount={riskRecordsCount?.entranceCriteriaCount}
                  setPollingRate={setPollingRate}
                  showAnalyzeRisks={showAnalyzeRisks}
                />
              )}
            </React.Fragment>
          ),
          headerTop: (
            <React.Fragment>
              {showHeaderTop === true && (
                <Layout.Group space={Number(themeConfiguration?.theme?.space?.sm) || 8}>
                  {showTotalRecords === true && (
                    <TotalRecords riskRecordCount={riskRecordsCount?.entranceCriteriaCount} />
                  )}

                  {showTotalRecords === true && allowExport === true && (
                    <Divider height={24} orientation="vertical" width={1} />
                  )}

                  {allowExport === true && <ExportStatus exportDetails={exportData} />}

                  {showFilters === true && [
                    <React.Fragment key="additional-criteria">
                      {!isEmpty(criteria?.entrance?.additional) && [
                        <Divider height={24} key="divider-additional" orientation="vertical" width={1} />,
                        <AppliedFilterCriteria
                          filters={criteria?.entrance?.additional}
                          key="applied-additional"
                          label="Additional Criteria"
                        />,
                      ]}
                    </React.Fragment>,
                    <React.Fragment key="common-criteria">
                      {!isEmpty(criteria?.entrance?.commonIds) && [
                        <Divider height={24} key="divider-common" orientation="vertical" width={1} />,
                        <AppliedFilterCriteria criteria={criteria} key="applied-common" label="Common Criteria" />,
                      ]}
                    </React.Fragment>,
                  ]}
                </Layout.Group>
              )}
            </React.Fragment>
          ),
          isLoading: isLoading || isFetching || riskCodesLoading || riskCodesFetching,
          requestArgs: {
            criteria:
              context === 'entrance'
                ? {
                    entrance: criteria?.entrance,
                  }
                : criteria,
            page: 0,
            size: 25,
            sort: 'eid,asc',
          },
          requestFunction: ListRiskRecords,
          requestKey: ListRiskRecords,
          rows: data?.content,
          totalPages: data?.totalPages,
          totalRecords: data?.totalElements,
        }}
      />
    </ErrorHandler>
  );
}
